/**
 * Remote Toggles Configuration - Feature Toggling, Gradual Rollouts, and Permanent Toggles
 *
 * Use this object to define all feature toggles used in the application.
 * Each toggle must follow the `RemoteToggleType` structure.
 *
 * Example:
 * ```
 * ONTARIO_HEADER: {
 *   key: 'toggle_ontario_header',
 *   trackingKey: 'kjca420',
 *   defaultValue: {
 *     int: true,
 *     prod: false,
 *   },
 * }
 * ```
 * **Key Guidelines:**
 * - `key`: Must start with the prefix `toggle_` followed by a descriptive name for the feature flag.
 * - `trackingKey`: A key prefixed by `kjca` used for tracking. Should align with story JIRA ticket. Optional for Toggles.
 * - `defaultValue`: An object defining the default/fallback values for both integration (`int`) and production (`prod`) environments.
 */
import { type RemoteToggleType } from '@/lib/firebase/types'

export const RemoteToggles = {
  VERTICAL_AUTOS_BRP_TOGGLE: {
    key: 'toggle_vertical_autos_brp',
    defaultValue: { int: true, prod: false },
  },
  SEARCH_AUTOCOMPLETE_TOGGLE: {
    key: 'toggle_search_autocomplete_query',
    defaultValue: { int: false, prod: false },
  },
  AUTOS_LANDING_PAGE_LINK: {
    key: 'toggle_autos_landing_page_link',
    defaultValue: { int: false, prod: false },
  },
  BEST_MATCH_ALGORITHM_TEST_TOGGLE: {
    key: 'toggle_exp_best_match_algorithm',
    defaultValue: { int: false, prod: false },
  },
  SRP_DEDUPLICATION_TEST_TOGGLE: {
    key: 'toggle_srp_deduplication',
    defaultValue: { int: false, prod: false },
    trackingKey: 'kjca5530',
  },
  SELLER_RESPONSIVENESS_TEST_TOGGLE: {
    key: 'toggle_exp_seller_responsiveness',
    defaultValue: { int: false, prod: false },
  },
  IMAGELESS_DOWN_BOOSTING_TEST_TOGGLE: {
    key: 'toggle_exp_imageless_down_boosting',
    defaultValue: { int: false, prod: false },
  },
  NAVIGATION_SRP: {
    key: 'toggle_navigation_srp',
    defaultValue: { int: false, prod: false },
  },
  PRICEDROP_NOTIFICATION: {
    key: 'toggle_pricedrop_notification',
    defaultValue: { int: false, prod: false },
  },
  EXTENDED_SEARCH_FOR_NULL_AND_LOW_SRP: {
    key: 'toggle_extended_search_for_null_and_low_srp',
    defaultValue: { int: true, prod: false },
  },
  ONE_TIME_SAVED_SEARCH: {
    key: 'toggle_one_time_saved_search',
    defaultValue: { int: true, prod: true },
  },
  REMOVE_ADSENSE_MWEB: {
    key: 'toggle_remove_adsense_mweb',
    defaultValue: { int: false, prod: false },
    trackingKey: 'kjca6246',
  },
  SEARCH_NEARBY_LOCATION: {
    key: 'toggle_search_nearby_location',
    defaultValue: { int: false, prod: false },
  },
  SRP_CONTROLS_MWEB_REDESIGN: {
    key: 'toggle_srp_controls_mweb_redesign',
    defaultValue: { int: false, prod: false },
  },
  SRP_THIN_CONTENT_LINKS: {
    key: 'toggle_srp_thin_content_links',
    defaultValue: { int: false, prod: false },
  },
  SEO_SEARCH_LANDING_PAGES: {
    key: 'toggle_seo_search_landing_pages',
    defaultValue: { int: false, prod: false },
  },
  SRP_GRID_VIEW_TOGGLE: {
    key: 'toggle_srp_grid_view',
    defaultValue: { int: false, prod: false },
    trackingKey: 'kjca6055',
  },
  WHITELIST_API: {
    key: 'toggle_whitelist_api',
    defaultValue: { int: true, prod: false },
  },
  CUSTOM_GTM_SCRIPT_URL: {
    key: 'toggle_custom_gtm_script_url',
    defaultValue: { int: false, prod: false },
  },
} as const satisfies RemoteToggleType
